import {useEffect, useRef, useState} from 'react';
import {Check, CirclePause, RefreshCw, ShieldCheck} from 'lucide-react';
import {api} from './lib/api';
import {activationBlockerText, activationCopy, activationFeatures, normalizeActivationStatus, readyActivationBatch, type ActivationFeature, type ActivationStatus} from './data/owner-activation';
import './owner-activation.css';

type Props = {onChanged?: () => void; initialStatus?: ActivationStatus};

export default function OwnerActivationPanel({onChanged, initialStatus}: Props) {
  const [status, setStatus] = useState<ActivationStatus | null>(initialStatus ?? null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const alive = useRef(false);
  const locked = useRef(false);
  const pending = useRef<AbortController | null>(null);
  const message = (cause: unknown) => cause instanceof Error ? cause.message : 'Operazione non riuscita. Riprova.';

  async function refresh(signal?: AbortSignal) {
    const next = normalizeActivationStatus(await api.request('/admin/activation', {signal}));
    if (alive.current && !signal?.aborted) setStatus(next);
    return next;
  }

  useEffect(() => {
    alive.current = true;
    const controller = new AbortController();
    if (!initialStatus) void refresh(controller.signal).catch(cause => {
      if (!controller.signal.aborted) setError(message(cause));
    });
    return () => {alive.current = false; controller.abort(); pending.current?.abort();};
  }, [initialStatus]);

  async function verify() {
    if (locked.current) return;
    locked.current = true; setBusy(true); setError(''); setNotice('');
    const controller = new AbortController(); pending.current = controller;
    try {await refresh(controller.signal);}
    catch (cause) {if (alive.current && !controller.signal.aborted) setError(message(cause));}
    finally {locked.current = false; if (alive.current) setBusy(false);}
  }

  async function save(features: Partial<Record<ActivationFeature, boolean>>) {
    if (locked.current || !Object.keys(features).length) return;
    locked.current = true; setBusy(true); setError(''); setNotice('');
    const controller = new AbortController(); pending.current = controller;
    try {
      const value = await api.request<{saved?: boolean; status?: unknown}>('/admin/activation', {method: 'POST', body: JSON.stringify({features}), signal: controller.signal});
      if (value.saved !== true) throw new Error('Salvataggio non confermato. Aggiorna la verifica prima di riprovare.');
      const next = normalizeActivationStatus(value.status);
      if (alive.current && !controller.signal.aborted) {
        setStatus(next); setNotice('Impostazioni salvate e controllate. I pagamenti non sono stati modificati.');
        api.invalidateConfig(); onChanged?.();
      }
    } catch (cause) {
      if (alive.current && !controller.signal.aborted) {
        setError(message(cause));
        // A timeout may have happened after a successful write; read the actual state.
        try {await refresh(controller.signal);} catch { /* Keep the last confirmed state and the visible error. */ }
      }
    } finally {locked.current = false; if (alive.current) setBusy(false);}
  }

  const available = status ? readyActivationBatch(status) : {};
  const enabledCount = status ? activationFeatures.filter(feature => status.features[feature].enabled).length : 0;

  return <section className="owner-activation panel" aria-labelledby="owner-activation-title">
    <div className="owner-activation-heading"><div><span className="owner-activation-eyebrow">Riservato al titolare</span><h2 id="owner-activation-title">Apri le funzioni di Gruvo.</h2>
      <p>Attiva ciò che è pronto. I controlli mancanti restano visibili e non vengono saltati.</p></div>
      <button className="button button-outline" disabled={busy} onClick={() => void verify()}><RefreshCw size={16} aria-hidden="true"/>Aggiorna verifica</button></div>
    <div className="owner-activation-note"><ShieldCheck size={20} aria-hidden="true"/><p>Questa sezione non apre gli acquisti, non effettua addebiti e non conferma documenti o verifiche al tuo posto. Il collegamento Google richiede anche una prova di accesso effettiva.</p></div>
    {status ? <>
      <p className="owner-activation-summary" role="status">{enabledCount} funzioni attive su {activationFeatures.length}.</p>
      <div className="owner-activation-list">{activationFeatures.map(feature => {
        const item = status.features[feature];
        return <article className={`owner-activation-item${item.enabled ? ' is-active' : ''}`} key={feature}>
          <div className="owner-activation-item-heading"><h3>{activationCopy[feature].title}</h3><span className={`owner-activation-state${item.enabled ? ' is-active' : ''}`}>
            {item.enabled ? <><Check size={14} aria-hidden="true"/>Attiva</> : item.requested ? 'Richiesta · controlli mancanti' : item.prerequisitesPassed ? 'Pronta da attivare' : 'Da completare'}</span></div>
          <p>{activationCopy[feature].description}</p>
          {item.blockers.length > 0 && <ul className="owner-activation-blockers" aria-label={`Da completare per ${activationCopy[feature].title}`}>{item.blockers.map(code => <li key={code}>{activationBlockerText(code)}</li>)}</ul>}
          <button className={`button ${item.requested ? 'button-outline' : 'button-dark'}`} disabled={busy || (!item.requested && !item.prerequisitesPassed)}
            aria-label={`${item.requested ? 'Sospendi' : 'Attiva'} ${activationCopy[feature].title}`} onClick={() => void save({[feature]: !item.requested})}>
            {item.requested && <CirclePause size={15} aria-hidden="true"/>}{item.requested ? 'Sospendi' : item.prerequisitesPassed ? 'Attiva' : 'Completa prima i controlli'}</button>
        </article>;
      })}</div>
      {Object.keys(available).length > 0 && <button className="button button-dark owner-activation-batch" disabled={busy} onClick={() => void save(available)}>Attiva le funzioni pronte ({Object.keys(available).length})</button>}
      <aside className="owner-activation-workers"><h3>Invii e operazioni automatiche</h3><p>La coda delle e-mail è predisposta, ma l’esecuzione periodica sul server non è ancora verificata. Attivare la coda non garantisce da solo che i messaggi vengano inviati quando nessuno visita il sito.</p><p>Gli accrediti automatici ai venditori hanno controlli separati e non si attivano da qui.</p></aside>
    </> : <p role="status">Verifica delle funzioni e degli archivi…</p>}
    {busy && <p role="status">Controllo e salvataggio in corso…</p>}
    {notice && <p className="owner-activation-success" role="status">{notice}</p>}
    {error && <p className="form-error" role="alert">{error}</p>}
  </section>;
}
